import { useState, useEffect, useRef, useCallback } from "react";
import { getTimerSettings } from "../../settings/hooks/use-timer-settings";
import { playSound } from "../../settings/utils/sound-presets";
import { track } from "../../../lib/analytics";

export interface StopwatchState {
  readonly isRunning: boolean;
  readonly elapsed: number;
  readonly taskId: string | null;
  readonly taskTitle: string;
  readonly laps: readonly number[];
}

const STORAGE_KEY = "palnia_stopwatch";

const DEFAULT_STATE: StopwatchState = {
  isRunning: false,
  elapsed: 0,
  taskId: null,
  taskTitle: "",
  laps: [],
};

function loadFromStorage(): StopwatchState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_STATE;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return DEFAULT_STATE;
    const obj = parsed as Record<string, unknown>;
    return {
      isRunning: false,
      elapsed: typeof obj.elapsed === "number" ? obj.elapsed : 0,
      taskId: typeof obj.taskId === "string" ? obj.taskId : null,
      taskTitle: typeof obj.taskTitle === "string" ? obj.taskTitle : "",
      laps: Array.isArray(obj.laps) ? obj.laps : [],
    };
  } catch {
    return DEFAULT_STATE;
  }
}

function persistState(state: StopwatchState): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export function useStopwatch() {
  const [state, setState] = useState<StopwatchState>(loadFromStorage());
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!state.isRunning) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }

    intervalRef.current = setInterval(() => {
      setState((prev) => {
        const updated = { ...prev, elapsed: prev.elapsed + 100 };
        persistState(updated);
        return updated;
      });
    }, 100);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [state.isRunning]);

  const start = useCallback((taskId?: string | unknown, taskTitle?: string) => {
    const safeTaskId = typeof taskId === "string" ? taskId : undefined;
    const safeTitle = typeof taskTitle === "string" ? taskTitle : undefined;
    setState((prev) => {
      const updated: StopwatchState = {
        ...prev,
        isRunning: true,
        taskId: safeTaskId ?? prev.taskId,
        taskTitle: safeTitle ?? prev.taskTitle,
      };
      persistState(updated);
      return updated;
    });
    track("timer_started", { type: "stopwatch" });
  }, []);

  const pause = useCallback(() => {
    setState((prev) => {
      const updated: StopwatchState = { ...prev, isRunning: false };
      persistState(updated);
      return updated;
    });
    track("timer_paused", { type: "stopwatch" });
  }, []);

  const reset = useCallback(() => {
    setState(DEFAULT_STATE);
    persistState(DEFAULT_STATE);
    track("timer_reset", { type: "stopwatch" });
  }, []);

  const lap = useCallback(() => {
    const { stopwatch } = getTimerSettings();
    playSound(stopwatch.lapSound);
    setState((prev) => {
      const updated: StopwatchState = {
        ...prev,
        laps: [...prev.laps, prev.elapsed],
      };
      persistState(updated);
      return updated;
    });
  }, []);

  const formatTime = useCallback((ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const base = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    const { stopwatch } = getTimerSettings();
    if (stopwatch.showMilliseconds) {
      const centiseconds = Math.floor((ms % 1000) / 10);
      return `${base}.${String(centiseconds).padStart(2, "0")}`;
    }
    return base;
  }, []);

  return {
    state,
    start,
    pause,
    reset,
    lap,
    formatTime,
  };
}
